import type { CSSProperties } from "react";
import { MAX_ATTEMPTS, WORD_LENGTH, type GuessEntry } from "@mk/shared";
import type { DraftCells } from "../features/boardDraft.ts";
import { Tile } from "./Tile.tsx";
import { messages } from "../i18n/index.ts";

interface BoardProps {
  rows: readonly GuessEntry[];
  /** Aktif satırın hücreleri; boş olanlar undefined. */
  cells: DraftCells;
  /** Oyuncunun seçtiği kutucuk — yazılan harf burayı değiştirir. */
  pinned?: number | null;
  onSelectCell?: (index: number) => void;
  /** Geçersiz tahminde aktif satır titrer. */
  shake: boolean;
  won: boolean;
  /** Sunucunun izin verdiği toplam tahmin sayısı. */
  attemptLimit?: number;
  /** Bu maçta çizilecek toplam satır sayısı. */
  maxAttempts?: number;
}

export function Board({
  rows,
  cells,
  pinned = null,
  onSelectCell,
  shake,
  won,
  maxAttempts = MAX_ATTEMPTS,
  attemptLimit = maxAttempts,
}: BoardProps) {
  const activeRow = rows.length;

  return (
    <div
      className="board"
      role="grid"
      aria-label={messages.board.label}
      style={{ ["--board-rows"]: maxAttempts } as CSSProperties}
    >
      {Array.from({ length: maxAttempts }, (_, r) => {
        const guess = rows[r];
        const locked = !guess && r >= Math.max(rows.length, attemptLimit);
        const isActive = !won && !locked && r === activeRow;
        const isWinningRow = won && r === rows.length - 1;

        const classes = ["board-row"];
        if (isActive && shake) classes.push("row-invalid");
        if (isWinningRow) classes.push("row-won");
        if (locked) classes.push("row-locked");

        return (
          <div key={r} className={classes.join(" ")} role="row">
            {Array.from({ length: WORD_LENGTH }, (_, c) => (
              <Tile
                key={c}
                index={c}
                letter={guess ? guess.word[c] : isActive ? cells[c] : undefined}
                state={guess?.pattern[c]}
                selected={isActive && pinned === c}
                onSelect={isActive && onSelectCell ? onSelectCell : undefined}
                locked={locked}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
